import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  line, outsideCallsRoute, BILLING_SITUATION_LABELS, Color, DEFAULT_CURRENCY_CODE, fill, NOT_COMPLETE_REASONS, NOT_COMPLETE_REASON_LABELS, REQUIRES_HIGHER_SKILL_LEVEL, Size, WORK_ORDER_ACTION_KEYS,
  LIST_PAGES, RECORD_ROUTES, formatCurrency, roundHalfUpCents, formatDateTime, printFamilyOf, printPartyBlock, printPartyLines, searchWithin, subSkillWarningText, t, wallClockInZone,
  type BillingSituation, type StringKey, type Chip, type NotCompleteReason, type PlannedPartRow, type PrintDocument, type PrintLineColumn, type WorkOrderAction, type VisitLineOption, type WorkOrderVisitRow, type WorkOrderVisitsAnswer, type WorkOrderDeletion, printDocumentTitle, visitIsOpen,
} from '@bms2/shared';
import { AlertBanner, Button, Chips, DatePicker, Select, TextInput } from '../../components/controls';
import { WorkOrderOnSite } from './WorkOrderOnSite';
import { WorkOrderJobType } from './WorkOrderJobType';
import { WorkOrderPlannedParts } from './WorkOrderPlannedParts';
import { WorkOrderPartWindow, type PartWindowStart } from './WorkOrderPartWindow';
import { Icon } from '../../components/Icon';
import { FormFrame } from '../../components/forms';
import { INVOICES_PATH, get, post, patch, del, useApi } from './api';
import styles from './RecordPage.module.css';
import { Money } from '../../components/Money';
import { ApprovalDecision } from '../approvals/ApprovalDecision';
import { WorkOrderLineBudget, WorkOrderLineField } from './WorkOrderLineField';
import { WorkOrderConfirmation } from './WorkOrderConfirmation';
import { WorkOrderOnCall } from './WorkOrderOnCall';
import { useAddNew } from './AddNewHost';
import { EditVisitModal, VisitMovableFields, fixedOf, fixedWaits, workOrderVisitsPath } from './WorkOrderVisits';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const WORK_ORDERS_PATH = '/work-orders';
/** 🔒 Delete on a job made by mistake (decision 12700). */
export const workOrderDeletePath = (workOrderId: string) => `${WORK_ORDERS_PATH}/${workOrderId}/delete`;
/** 🔒 Decision 12970: what the Delete window reads when it opens, and the `admin.operations` holder's Delete Anyway. */
export const workOrderDeletionPath = (workOrderId: string) => `${WORK_ORDERS_PATH}/${workOrderId}/deletion`;
export const workOrderDeleteAnywayPath = (workOrderId: string) => `${WORK_ORDERS_PATH}/${workOrderId}/delete-anyway`;
/** 🔒 Registered with the path guard: the invoice raised from a finished job (decision 2594; T1003, T1171). */
export const invoiceFromWorkOrderPath = (workOrderId: string): string => `${INVOICES_PATH}/from-work-order/${workOrderId}`;

/** T3819 (piece 29): the Travel tile's facts as the server reads them; the tile leaves out any that are null. */
export interface TravelFactsView {
  originAddress: string | null;
  siteAddress: string | null;
  lookup: { oneWayMinutes: number; distanceMeters: number | null; service: string | null; at: string } | null;
  roundingIncrementMinutes: number | null;
  entered: { byName: string | null; at: string; replacedMinutes: number | null; replacedSource: string | null } | null;
  billing: { billed: boolean; reason: 'agreementCovers' | 'agreementCap' | 'followUpNotBillable' | null; capMinutes: number | null };
  /** Whether the viewer may open the Outside Calls page; the button is drawn only when true. */
  mayOpenOutsideCalls?: boolean;
}

const METERS_PER_MILE = 1609.344;
const SERVICE_LABEL: Readonly<Record<string, StringKey>> = {
  GoogleMaps: 'settings.zohoBooks.calls.provider.googleMaps',
  OpenStreetMap: 'settings.zohoBooks.calls.provider.openStreetMap',
  Geoapify: 'settings.zohoBooks.calls.provider.geoapify',
};
const BILLING_LABEL: Readonly<Record<NonNullable<TravelFactsView['billing']['reason']>, StringKey>> = {
  agreementCovers: 'workOrder.travel.absorbedAgreement',
  agreementCap: 'workOrder.travel.billedCapped',
  followUpNotBillable: 'workOrder.travel.absorbedFollowUp',
};

/** A worker's participation as the document carries it; `costRateCents` is absent for a viewer the rule refuses. */
export interface WorkerRow {
  id: string;
  personId: string;
  personName: string;
  hours: number;
  billingRateCents: number;
  costRateCents?: number;
  isBillable: boolean;
  requiredSkillLevelName: string;
  skillLevelName: string | null;
  mismatch: 'Higher' | 'Lower' | null;
  /** Decision 0464: what this line needs that this person is not recorded as able to do. */
  missingSubSkills: { id: string; name: string }[];
  /** 🔒 Decision 2282: the certifications the job's checklists ask for that this person holds no Current completion of; absent from an older reply. */
  missingTraining?: string[];
  billedCents: number;
  /** 🔒 Piece 29: the per-worker requirement gate; absent from an older reply. */
  gate?: { ok: boolean; reason: string | null };
}

export interface LineRow {
  id: string;
  description: string;
  completionStatus: string;
  notCompleteReason: string | null;
  missingSkillName: string | null;
  requiredSkillLevelName: string;
  priorityModifier: number;
  afterHoursModifier: number;
  composedFactor: number;
  isBillable: boolean;
  lineHours: number;
  billedCents: number;
  situation: 'A' | 'B' | 'C' | null;
  flags: string[];
  requiredSubSkills: { id: string; name: string; skillName: string }[];
  workers: WorkerRow[];
  /** Piece 29: the line's own due date, Asset and budget; each absent from an older reply. */
  dueDate?: string | null;
  assetId?: string | null;
  assetName?: string | null;
  budgetedHours?: number | null;
  actualHours?: number;
  remainingHours?: number | null;
  overrun?: boolean;
}

export interface WorkOrderView {
  id: string;
  /** The version the page read, which Hold and Resume send back so a stale page is refused (piece 7). */
  version?: number;
  workOrderNumber: string;
  status: string;
  companyId: string;
  /** The document's own currency, so a rate and an amount are drawn in it rather than assumed dollars. */
  currencyCode?: string;
  companyName: string;
  siteId: string;
  siteName: string;
  scheduledStart: string | null;
  priorityTierName: string;
  lines: LineRow[];
  materials: { id: string; itemId?: string; sku: string; itemName: string; quantity: number; unitName: string }[];
  /** The shelf a part fitted here comes off, the server's own answer (decision 0530); null where the job names none. */
  travel: {
    originSiteName: string;
    billedTravelMinutes: number | null;
    travelSource: string | null;
    travelOverrideReason: string | null;
    resolved: boolean;
    problem: string | null;
    line: { description: string; hours: number; rateCents: number; amountCents: number } | null;
    agreementCoversTravel: boolean;
    agreementCapMinutes: number | null;
    /** T3819: what the tile says beyond the figure; absent from an older reply, and each fact null where nothing stores it. */
    facts?: TravelFactsView;
  };
  totals: { labourHours: number; billedLabourCents: number; billedHours: number; minimumSaleQuantity: number | null; tripChargeCents: number | null };
  actions: WorkOrderAction[];
  editable: boolean;
  /** 🔒 Piece 29 (T1300): the job On Hold, its reason, note and how long it has stood; null where it is not held, absent from an older reply. */
  hold?: { reason: string; note: string | null; heldAt: string; heldMinutes: number } | null;
  /** The four reasons the picker offers, the server's own list; absent from an older reply. */
  holdReasons?: string[];
  /** 🔒 Piece 29: dispatched while its approval is Pending, so the approval is a retrospective review (decision 0734); absent from an older reply. */
  retrospective?: boolean;
  /** 🔒 Piece 29: the check-in pairs' on-site hours beside the labour hours (decision 0229); absent from an older reply. */
  onSite?: { onSiteHours: number; labourHours: number; pairs: number };
  /** Piece 29: the Deal's origin, which draws the customer's text confirmation on an emergency; absent from an older reply. */
  dealOrigin?: string | null;
  /** 🔒 Decision 2594 (T1175): the job's billable work to date past the customer PO's amount; null inside it, absent from an older reply. */
  customerPoWarning?: string | null;
  /** 🔒 Decision 2611 (T1170): what a Complete job has not invoiced, written out; null where nothing is owed, absent from an older reply. */
  unbilledText?: string | null;
  /** 🔒 Decision 2611: a job closed without billing, why, who and when; null on any other, absent from an older reply. */
  nonBillable?: { reason: string; byName: string | null; at: string } | null;
  priorityTierId?: string;
  /** 🔒 Piece 29 (T1710): the job type the job was pre-filled from, read by name even once deactivated; absent from an older reply. */
  jobType?: { id: string; name: string; isActive: boolean; estimatedMinutes: number | null } | null;
  /** 🔒 Piece 29 (T1710): the parts the job expects to use, an expectation that moves no stock; absent from an older reply. */
  plannedParts?: PlannedPartRow[];
}

/**
 * A rate or an amount in the document's own currency. 🔨 It goes through the
 * shared formatter, so it groups its thousands and carries the document's
 * code; the hand-rolled `$${n.toFixed(2)}` said dollars whatever the document
 * said and printed 2800 as $2800.00 (found in review, 2026-09-15).
 */
const moneyIn = (currencyCode: string) => (amountCents: number) => formatCurrency({ amount: amountCents, currencyCode });
/** 🔒 The same cents as the Money component, for a figure the screen draws (decision 0717); the print projection above keeps its text. */
function cashIn(currencyCode: string) {
  return function cash(amountCents: number) {
    return <Money cents={amountCents} currencyCode={currencyCode} />;
  };
}

/**
 * 🔨 The Work Order prints in the Field family, so Region 8 is a different
 * renderer: its lines are labour against an asset, and the manufacturer part
 * and the discount of the Transactional set mean nothing on one (piece 27;
 * decision 0409).
 */
export const WORK_ORDER_PRINT_COLUMNS: readonly PrintLineColumn[] = ['lineNumber', 'item', 'description', 'qty', 'unitPrice', 'amount'];

/**
 * The job as the print layout draws it: our own name, the customer, the site,
 * then the labour lines and their total. 🔨 The scheduled start is a stored
 * instant and is drawn as a wall clock in the viewer's own zone, never as the
 * raw string (decision 0261).
 *
 * 🔨 The sheet spends the same formatter as the panels above it, in the
 * document's own currency (found on the merge, 2026-09-15). The projection had
 * its own, which writes a bare `$` whatever the code says and drops the cents
 * on a whole amount, so a job billed at $1,200.00 on screen printed $1,200.
 */
export function workOrderPrintDocument(facts: Readonly<Record<string, string | boolean | null>>, document: WorkOrderView, timeZone: string): PrintDocument {
  const text = (key: string) => (typeof facts[key] === 'string' ? (facts[key] as string) : '');
  const startsAt = document.scheduledStart ? wallClockInZone(document.scheduledStart, timeZone).replace('T', ' ') : '';
  const money = moneyIn(document.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const site = printPartyBlock(facts.siteBlock);
  return {
    documentType: 'WorkOrder',
    family: printFamilyOf('WorkOrder'),
    title: printDocumentTitle('WorkOrder'),
    number: document.workOrderNumber,
    issuer: { heading: t('document.print.issuedBy'), name: text('organizationName'), lines: printPartyLines(facts.issuerAddress) },
    recipient: { heading: t('document.print.customer'), name: document.companyName || text('companyName'), lines: printPartyLines(facts.customerAddress) },
    // 🔒 T3782: the Site block the server stacked through the print's own rule, drawn as sent, so the sheet is the PDF's.
    deliverTo: site ? { heading: t('document.print.site'), ...site } : null,
    information: [
      { label: t('workOrder.new.start'), value: startsAt },
      { label: t('workOrder.print.priority'), value: document.priorityTierName ?? '' },
      { label: t('workOrder.print.status'), value: document.status },
    ].filter((fact) => fact.value !== ''),
    notice: null,
    lines: document.lines.map((row, i) => ({
      lineNumber: String(i + 1),
      item: row.requiredSkillLevelName,
      description: row.description,
      mfrPart: '',
      qty: `${row.lineHours} h`,
      unitPrice: row.lineHours > 0 ? money(roundHalfUpCents(row.billedCents / row.lineHours)) : '',
      discount: '',
      amount: money(row.billedCents),
    })),
    totals: [{ label: t('document.print.total'), value: money(document.totals.billedLabourCents) }],
    terms: [],
  };
}

interface OptionsAnswer {
  sites: { id: string; label: string }[];
  deals: { id: string; label: string; dealType: string }[];
  agreements: { id: string; label: string }[];
  levels: { id: string; name: string; level: number; labourItemId: string | null }[];
  reasonCodes: { id: string; code: string; label: string; isBillable: boolean }[];
  skills: { id: string; name: string }[];
  /** Decision 0464: the capabilities beneath a skill, which a line asks for by name. */
  subSkills: { id: string; name: string; skillName: string }[];
  workers: { employmentId: string; personId: string; name: string; title: string | null; skillLevelName: string | null; assignable: boolean; hasLogin?: boolean }[];
  /** Piece 29: the tiers in force, which a manager's override picks from; absent from an older reply. */
  tiers?: { id: string; name: string; maxNoticeHours: number; billingModifier: number }[];
}

/** The write a window sent, as the window needs to read it. */
type Outcome = { ok: true } | { ok: false; message: string };

interface PanelProps {
  document: WorkOrderView;
  apiBaseUrl?: string;
  onChanged: () => void;
  /** 🔒 Carries the refusal's whole answer beside its sentence, so a panel can pass the route the page draws as a link (T3511). */
  onError: (message: string, body?: unknown) => void;
}

/** One options read per Work Order page: the lines panel and the footer both ask, and both want the same list. */
const OptionsContext = createContext<ReturnType<typeof useApi<OptionsAnswer>> | null>(null);

const optionsPath = (companyId: string | null) => (companyId ? `${WORK_ORDERS_PATH}/options?companyId=${encodeURIComponent(companyId)}` : null);

export function WorkOrderOptions({ companyId, apiBaseUrl = '', children }: { companyId: string | null; apiBaseUrl?: string; children: ReactNode }) {
  const options = useApi<OptionsAnswer>(optionsPath(companyId), apiBaseUrl);
  return <OptionsContext.Provider value={options}>{children}</OptionsContext.Provider>;
}

/**
 * What the New Work Order modal and the page's pickers read, for one company.
 * 🔨 No company, no read: the path was built without one and fetched anyway,
 * so every Company and Site page asked for the options of nobody and took a
 * silent refusal for a viewer with no read on work orders (found in review,
 * 2026-09-15). Inside a `WorkOrderOptions` the page's one read is shared, so
 * the panel and the footer ask once between them.
 */
export function useWorkOrderOptions(companyId: string | null, apiBaseUrl = '') {
  const shared = useContext(OptionsContext);
  const own = useApi<OptionsAnswer>(shared ? null : optionsPath(companyId), apiBaseUrl);
  return shared ?? own;
}

/**
 * The labour lines (piece 22): each line with the level it demands, the hours
 * rolled up from its workers, and the workers themselves as removable chips.
 * 🔨 The hours are a worker's, never the line's: a helper at one rate and an
 * engineer at another on one line is two chips and one roll-up, which a single
 * per-line rate cannot reproduce. `costRateCents` is drawn only where the payload
 * carried it, which is HR, Admin and that worker.
 */
export function WorkOrderLines({ document, apiBaseUrl = '', onChanged, onError }: PanelProps) {
  const navigate = useNavigate();
  const cash = cashIn(document.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const options = useWorkOrderOptions(document.companyId, apiBaseUrl);
  const answer = options.state.kind === 'ready' ? options.state.data : null;
  const [description, setDescription] = useState('');
  const [levelId, setLevelId] = useState('');
  const [assigning, setAssigning] = useState<{ lineId: string; employmentId: string; hours: string } | null>(null);
  const [stopping, setStopping] = useState<{ lineId: string; reason: string; skillId: string } | null>(null);
  /** The sub-skill being added, keyed by the line it goes on, so two open lines never share a draft. */
  const [subSkillDraft, setSubSkillDraft] = useState<Record<string, string>>({});
  /** 🔒 Decision 3063: a refused write reads inside the window that sent it, which stays open for the correction. */
  const [refusal, setRefusal] = useState<string | null>(null);

  const wrote = async (outcome: Outcome) => {
    if (outcome.ok) onChanged();
    else onError(outcome.message);
  };
  /** A window's own write: refused, it reads inside the window; written, the window closes and the page reads again. */
  const settle = async (outcome: Outcome, close: () => void) => {
    if (!outcome.ok) return setRefusal(outcome.message);
    setRefusal(null);
    close();
    onChanged();
  };
  const shut = (close: () => void) => () => {
    setRefusal(null);
    close();
  };
  const addLine = async () => {
    await wrote(await post(`${WORK_ORDERS_PATH}/${document.id}/lines`, { description, requiredSkillLevelId: levelId || null }, apiBaseUrl));
    setDescription('');
  };
  const addWorker = async () => {
    if (!assigning) return;
    await settle(await post(`${WORK_ORDERS_PATH}/${document.id}/lines/${assigning.lineId}/workers`, { employmentId: assigning.employmentId, hours: Number(assigning.hours) || 0 }, apiBaseUrl), () => setAssigning(null));
  };
  // Decision 0464: what this line's visit needs; naming one refuses nobody.
  const addSubSkill = async (lineId: string) => {
    const subSkillId = subSkillDraft[lineId] ?? '';
    if (!subSkillId) return;
    await wrote(await post(`${WORK_ORDERS_PATH}/${document.id}/lines/${lineId}/sub-skills`, { subSkillId }, apiBaseUrl));
    setSubSkillDraft((was) => ({ ...was, [lineId]: '' }));
  };
  const removeSubSkill = async (lineId: string, subSkillId: string) => {
    await wrote(await del(`${WORK_ORDERS_PATH}/${document.id}/lines/${lineId}/sub-skills/${subSkillId}`, apiBaseUrl));
  };
  const removeWorker = async (lineId: string, workerId: string) => {
    await wrote(await del(`${WORK_ORDERS_PATH}/${document.id}/lines/${lineId}/workers/${workerId}`, apiBaseUrl));
  };
  const complete = async (lineId: string) => {
    await wrote(await patch(`${WORK_ORDERS_PATH}/${document.id}/lines/${lineId}`, { completionStatus: 'Complete' }, apiBaseUrl));
  };
  const notNeeded = async (lineId: string) => {
    await wrote(await patch(`${WORK_ORDERS_PATH}/${document.id}/lines/${lineId}`, { completionStatus: 'NotNeeded' }, apiBaseUrl));
  };
  const stop = async () => {
    if (!stopping) return;
    await settle(await patch(`${WORK_ORDERS_PATH}/${document.id}/lines/${stopping.lineId}`, { completionStatus: 'NotComplete', notCompleteReason: stopping.reason, missingSkillId: stopping.skillId || null }, apiBaseUrl), () => setStopping(null));
  };

  /**
   * 🔨 A worker short of a sub-skill the line needs is warned about and never
   * refused (decision 0464). The banner names the person and what they are not
   * recorded as able to do, and its action opens their page, where it is
   * recorded — a banner stating a problem with no way to it is a decoration.
   */
  const shortWorkers = document.lines.flatMap((row) => row.workers.filter((worker) => worker.missingSubSkills.length > 0).map((worker) => ({ line: row, worker })));
  /**
   * 🔒 Decision 2282: a worker short of a certification the job's checklists ask
   * for is warned about once per job, never refused (the owner's answer of
   * 2026-10-07), and the action opens their page, where their training is.
   */
  const untrained = [...new Map(document.lines.flatMap((row) => row.workers.filter((worker) => (worker.missingTraining ?? []).length > 0).map((worker) => [worker.personId, worker] as const))).values()];

  return (
    <div data-work-order-lines={document.workOrderNumber}>
      {/* 🔒 Piece 29 (T1710): the job type the lines were pre-filled from, and the pick on a Draft. */}
      <WorkOrderJobType document={document} apiBaseUrl={apiBaseUrl} onChanged={onChanged} />
      {/* 🔒 Piece 29: an emergency dispatched before its approval says so, so approve-after never quietly becomes approve-never. */}
      {document.retrospective ? <AlertBanner tone="warning" message={t('workOrder.retrospective.banner')} action={{ label: t('workOrder.retrospective.open'), onAction: () => navigate('/approvals') }} /> : null}
      {/* 🔒 Piece 29: how long the crew was on site, from the check-in pairs, beside the labour booked; neither is worked out from the other. */}
      {document.onSite && document.onSite.pairs > 0 ? (
        <p className={styles.hint} data-work-order-on-site={document.onSite.onSiteHours}>{line(t('workOrder.onSite.label'), `${document.onSite.onSiteHours} h`, '·', t('workOrder.onSite.labour'), `${document.onSite.labourHours} h`)}</p>
      ) : null}
      {/* 🔒 T309 (decision 0873): the job's assigned workers checked in and out from its own page, which feeds the figure above. */}
      <WorkOrderOnSite workOrderId={document.id} apiBaseUrl={apiBaseUrl} onChanged={onChanged} />
      {/* 🔒 Piece 29: an emergency's customer text confirmation, on the job it authorises. */}
      {document.dealOrigin === 'Emergency' ? <WorkOrderConfirmation workOrderId={document.id} editable={document.editable} apiBaseUrl={apiBaseUrl} onError={onError} /> : null}
      {document.dealOrigin === 'Emergency' ? <WorkOrderOnCall workOrderId={document.id} apiBaseUrl={apiBaseUrl} /> : null}
      {shortWorkers.map(({ line: row, worker }) => (
        <AlertBanner
          key={`${row.id}:${worker.id}`}
          tone="warning"
          message={subSkillWarningText(worker.personName, worker.missingSubSkills, t('workOrder.line.subSkillMissingPhrase')) ?? ''}
          action={{ label: t('workOrder.line.subSkillOpenPerson'), onAction: () => navigate(`${RECORD_ROUTES.PersonRecord.list}/${worker.personId}`) }}
        />
      ))}
      {untrained.map((worker) => (
        <span key={`training:${worker.personId}`} data-worker-missing-training={worker.personName}>
          <AlertBanner
            tone="warning"
            message={`${worker.personName} ${t('workOrder.line.trainingMissingPhrase')} ${(worker.missingTraining ?? []).join(', ')}`}
            action={{ label: t('workOrder.line.subSkillOpenPerson'), onAction: () => navigate(`${RECORD_ROUTES.PersonRecord.list}/${worker.personId}`) }}
          />
        </span>
      ))}
      {document.lines.length === 0 ? <p className={styles.hint}>{t('record.tile.empty')}</p> : null}
      <ul className={styles.plainList}>
        {document.lines.map((row) => (
          <li key={row.id} className={styles.workLine} data-work-line={row.id} data-line-completion={row.completionStatus}>
            <p className={styles.sectionTitle}>{row.description}</p>
            <p className={styles.hint}>
              {line(row.requiredSkillLevelName, '·', `${row.lineHours} h`, '·')} {cash(row.billedCents)}{row.isBillable ? null : ` · ${t('workOrder.line.notBillable')}`}{row.situation ? ` · ${BILLING_SITUATION_LABELS[row.situation as BillingSituation]}` : null}
            </p>
            <WorkOrderLineBudget row={{ ...row, dueDate: row.dueDate ?? null }} />
            <WorkOrderLineField workOrderId={document.id} row={{ ...row, dueDate: row.dueDate ?? null }} editable={document.editable} apiBaseUrl={apiBaseUrl} onChanged={onChanged} onError={onError} />
            {/* Piece 29: a finished line and a line nobody needed say so in words, as a stopped one always did. */}
            {row.completionStatus === 'Complete' || row.completionStatus === 'NotNeeded' ? (
              <p className={styles.hint} data-line-status={row.completionStatus}>{row.completionStatus === 'Complete' ? t('workOrder.line.statusComplete') : t('workOrder.line.statusNotNeeded')}</p>
            ) : null}
            {row.completionStatus === 'NotComplete' && row.notCompleteReason ? (
              <p className={styles.problem} data-line-stop-reason={row.notCompleteReason}>{line(NOT_COMPLETE_REASON_LABELS[row.notCompleteReason as NotCompleteReason] ?? row.notCompleteReason, row.missingSkillName ? `· ${row.missingSkillName}` : '')}</p>
            ) : null}
            <span className={styles.chips} data-line-sub-skills={row.id}>
              <span className={styles.hint}>{row.requiredSubSkills.length === 0 ? t('workOrder.line.subSkillNone') : t('workOrder.line.subSkillsRequired')}</span>
              {row.requiredSubSkills.map((sub) => (
                <span key={sub.id} className={styles.chip} data-line-sub-skill={sub.name}>
                  <span>{line(sub.skillName, '·', sub.name)}</span>
                  {document.editable ? (
                    <button type="button" className={styles.clearFlag} aria-label={t('workOrder.line.subSkillRemove')} data-remove-sub-skill={sub.name} onClick={() => void removeSubSkill(row.id, sub.id)}>
                      <Icon meaning="remove" size="inline" />
                    </button>
                  ) : null}
                </span>
              ))}
            </span>
            {document.editable ? (
              <div className={styles.actions} data-add-line-sub-skill={row.id}>
                <Select size={Size.Medium}
                  label={t('workOrder.line.subSkillPick')}
                  value={subSkillDraft[row.id] ?? ''}
                  onChange={(value) => setSubSkillDraft((was) => ({ ...was, [row.id]: value }))}
                  placeholder={t('workOrder.new.noneYet')}
                  options={(answer?.subSkills ?? []).filter((sub) => !row.requiredSubSkills.some((had) => had.id === sub.id)).map((sub) => ({ value: sub.id, label: `${sub.skillName} · ${sub.name}` }))}
                  dataAttribute={{ 'data-line-sub-skill-choice': row.id }}
                />
                <Button size={Size.Small} text={[t('workOrder.line.subSkillAdd')]} actions={[() => void addSubSkill(row.id)]} disabled={!(subSkillDraft[row.id] ?? '')} />
              </div>
            ) : null}
            <span className={styles.chips} data-line-workers={row.id}>
              {row.workers.length === 0 ? <span className={styles.hint}>{t('workOrder.line.noWorkers')}</span> : null}
              {row.workers.map((worker) => (
                <span key={worker.id} className={styles.chip} data-line-worker={worker.personName} data-worker-mismatch={worker.mismatch ?? ''}>
                  <span>{line(worker.personName, '·', `${worker.hours} h`, '@')} {cash(worker.billingRateCents)}{worker.costRateCents === undefined ? null : <> {line('·', t('workOrder.line.cost'))} {cash(worker.costRateCents)}</>}</span>
                  {worker.mismatch ? <span className={styles.hint}>{worker.mismatch === 'Higher' ? t('workOrder.line.mismatchHigher') : t('workOrder.line.mismatchLower')}</span> : null}
                  {/* 🔨 Piece 29: the gate acts per worker, so a Blocked worker names the requirement and the worker beside them is untouched. */}
                  {worker.gate && !worker.gate.ok ? <span className={styles.problem} data-worker-gate={worker.personName}>{line(t('workOrder.gate.blocked'), worker.gate.reason ?? '')}</span> : null}
                  {worker.missingSubSkills.length > 0 ? (
                    <span className={styles.problem} data-worker-missing-sub-skills={worker.personName}>{worker.missingSubSkills.map((sub) => sub.name).join(', ')}</span>
                  ) : null}
                  {document.editable ? (
                    <button type="button" className={styles.clearFlag} aria-label={t('workOrder.line.remove')} data-remove-worker={worker.id} onClick={() => void removeWorker(row.id, worker.id)}><Icon meaning="remove" size="inline" /></button>
                  ) : null}
                </span>
              ))}
            </span>
            {document.editable ? (
              <div className={styles.actions}>
                <Button size={Size.Small} text={[t('workOrder.line.addWorker')]} actions={[() => setAssigning({ lineId: row.id, employmentId: '', hours: '' })]} />
                {row.completionStatus === 'Pending' ? <Button size={Size.Small} text={[t('workOrder.line.complete')]} actions={[() => void complete(row.id)]} /> : null}
                {row.completionStatus === 'Pending' ? <Button size={Size.Small} text={[t('workOrder.line.notComplete')]} actions={[() => setStopping({ lineId: row.id, reason: REQUIRES_HIGHER_SKILL_LEVEL, skillId: '' })]} /> : null}
                {/* 🔒 Piece 29: Not needed is its own status, never a Not complete: it rolls to no follow-up and consumes no part. */}
                {row.completionStatus === 'Pending' ? <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.line.notNeeded')]} actions={[() => void notNeeded(row.id)]} /> : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {document.editable ? (
        <div className={styles.actions} data-work-order-add-line="true">
          <TextInput size={Size.Large} label={t('workOrder.line.description')} value={description} onChange={setDescription} name="line-description" required />
          <Select size={Size.Medium}
            label={t('workOrder.line.level')}
            value={levelId}
            onChange={setLevelId}
            placeholder={t('workOrder.new.noneYet')}
            options={(answer?.levels ?? []).map((level) => ({ value: level.id, label: level.name }))}
            dataAttribute={{ 'data-line-level': 'new' }}
          />
          <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.line.add')]} actions={[() => void addLine()]} disabled={!description.trim()} />
        </div>
      ) : null}

      <FormFrame
        host="window"
        open={assigning !== null}
        title={t('workOrder.line.addWorker')}
        onCancel={shut(() => setAssigning(null))}
        dataForm="work-order-worker"
        typed={assigning !== null && (assigning.employmentId !== '' || assigning.hours !== '')}
        act={{ label: t('settings.add'), disabled: !assigning?.employmentId, onAct: () => addWorker() }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <Select size={Size.Medium}
          label={t('workOrder.line.worker')}
          value={assigning?.employmentId ?? ''}
          onChange={(value) => setAssigning((was) => (was ? { ...was, employmentId: value } : was))}
          placeholder={t('workOrder.new.noneYet')}
          // 🔨 A worker whose position names no level is listed and marked, never hidden: the dispatcher sees the gap rather than an empty list.
          options={(answer?.workers ?? []).map((worker) => ({ value: worker.employmentId, label: line(worker.name, worker.skillLevelName ? `· ${worker.skillLevelName}` : `· ${t('workOrder.line.unassignable')}`) }))}
          dataAttribute={{ 'data-worker-choice': 'true' }}
        />
        <TextInput size={Size.Small} label={t('workOrder.line.hours')} value={assigning?.hours ?? ''} onChange={(value) => setAssigning((was) => (was ? { ...was, hours: value } : was))} name="worker-hours" />
      </FormFrame>

      <FormFrame
        host="window"
        open={stopping !== null}
        title={t('workOrder.line.notComplete')}
        onCancel={shut(() => setStopping(null))}
        dataForm="work-order-stop"
        // The window opens on the higher-level reason with no skill named, so only a change from that is typed (decision 3061).
        typed={stopping !== null && (stopping.reason !== REQUIRES_HIGHER_SKILL_LEVEL || stopping.skillId !== '')}
        act={{ label: t('settings.save'), disabled: stopping?.reason === REQUIRES_HIGHER_SKILL_LEVEL && !stopping.skillId, onAct: () => stop() }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <Select size={Size.Large}
          label={t('workOrder.line.reason')}
          value={stopping?.reason ?? ''}
          onChange={(value) => setStopping((was) => (was ? { ...was, reason: value } : was))}
          options={NOT_COMPLETE_REASONS.map((reason) => ({ value: reason, label: NOT_COMPLETE_REASON_LABELS[reason] }))}
          dataAttribute={{ 'data-stop-reason': 'true' }}
        />
        {/* 🔨 A stop for a higher level names the skill the scope never named before it saves; the rate of re-classification is the number this produces. */}
        {stopping?.reason === REQUIRES_HIGHER_SKILL_LEVEL ? (
          <Select size={Size.Medium}
            label={t('workOrder.line.missingSkill')}
            value={stopping.skillId}
            onChange={(value) => setStopping((was) => (was ? { ...was, skillId: value } : was))}
            placeholder={t('workOrder.new.noneYet')}
            options={(answer?.skills ?? []).map((skill) => ({ value: skill.id, label: skill.name }))}
            dataAttribute={{ 'data-missing-skill': 'true' }}
            required
          />
        ) : null}
      </FormFrame>
    </div>
  );
}

/**
 * 🔒 The parts fitted on the visit (piece 22; build issue T35): the list, and
 * Add Part, which opens the window that asks which part and where it came off.
 * A Sales Order line they came from stays on the row.
 *
 * 🔒 Adding a part moves the stock it came off (decision 0530), and the window
 * offers the person's van first, the job's shop second and Another Site over
 * our own sites (decision 10320), each with its on-hand, so a refusal on an
 * empty site is read against the site the person picked.
 */
export function WorkOrderMaterials({ document, apiBaseUrl = '', onChanged, onError }: PanelProps) {
  const [busy, setBusy] = useState(false);
  /** The window's opening: null shut, `blank` from Add Part, or a planned part's item and quantity from its Record Fitted. */
  const [adding, setAdding] = useState<PartWindowStart | 'blank' | null>(null);
  const materialsPath = `${WORK_ORDERS_PATH}/${document.id}/materials`;

  const remove = async (materialId: string) => {
    setBusy(true);
    const outcome = await del(`${materialsPath}/${materialId}`, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) return onError(outcome.message, outcome.body);
    onChanged();
  };

  return (
    <div data-work-order-materials={document.workOrderNumber}>
      {/* 🔒 Piece 29 (T1710): the parts the job expects, offered first, each fitted through the same window (decision 10320). */}
      <WorkOrderPlannedParts document={document} apiBaseUrl={apiBaseUrl} onChanged={onChanged} onError={onError} onFit={(start) => setAdding(start)} />
      {document.materials.length === 0 ? <p className={styles.hint}>{t('workOrder.material.none')}</p> : null}
      <ul className={styles.plainList}>
        {document.materials.map((row) => (
          <li key={row.id} data-work-material={row.sku}>
            {line(row.sku, '·', row.itemName, '·', String(row.quantity), row.unitName)}
            {document.editable ? (
              <span data-work-material-remove={row.id}>
                <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.material.remove')]} actions={[() => void remove(row.id)]} disabled={busy} />
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {document.editable ? (
        <div className={styles.actions} data-work-order-add-material="true">
          <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.material.add')]} actions={[() => setAdding('blank')]} disabled={busy} />
        </div>
      ) : null}
      {/* 🔨 Mounted only while open and keyed to what it opens on, so each opening starts from its own part (piece 13). */}
      {adding ? (
        <WorkOrderPartWindow
          key={adding === 'blank' ? 'blank' : `${adding.itemId}-${adding.quantity}`}
          workOrderId={document.id}
          materialsPath={materialsPath}
          apiBaseUrl={apiBaseUrl}
          start={adding === 'blank' ? null : adding}
          onClose={() => setAdding(null)}
          onAdded={() => { setAdding(null); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

/**
 * T3819 (piece 29): under the figure, what the lookup found and how it was priced.
 * 🔨 A fact the server does not hold is left out, never drawn blank: an older
 * lookup with no distance shows no miles line.
 */
function TravelFacts({ document, facts }: { document: WorkOrderView; facts: TravelFactsView }) {
  const navigate = useNavigate();
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const travel = document.travel;
  const cash = moneyIn(document.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const when = (at: string) => formatDateTime(at, zone, dateFormat);
  const place = (name: string, address: string | null) => (address ? `${name}, ${address}` : name);
  const lookup = facts.lookup;
  const service = lookup?.service ? SERVICE_LABEL[lookup.service] : undefined;
  const miles = lookup?.distanceMeters ? Math.round((lookup.distanceMeters / METERS_PER_MILE) * 10) / 10 : null;
  const billed = travel.billedTravelMinutes;
  const hours = travel.line?.hours;
  const replaced = facts.entered?.replacedMinutes;
  const oneWay = lookup?.oneWayMinutes ?? null;
  const roundTrip = oneWay !== null && facts.roundingIncrementMinutes !== null ? Math.ceil((oneWay * 2) / facts.roundingIncrementMinutes) * facts.roundingIncrementMinutes : oneWay !== null ? Math.ceil(oneWay * 2) : null;
  // 🔨 The round-trip sentence is drawn only where the billed minutes are that round trip: an Agreement that covers travel bills none, and a cap bills the cap.
  let basis: string | null = null;
  if (billed !== null && !facts.entered) {
    if (facts.billing.reason === 'agreementCovers') basis = oneWay !== null ? fill(t('workOrder.travel.basisCovered'), { oneWay }) : t('workOrder.travel.basisCoveredNoLookup');
    else if (facts.billing.reason === 'agreementCap' && (oneWay === null || roundTrip === null || billed < roundTrip)) basis = oneWay !== null ? fill(t('workOrder.travel.basisCapped'), { minutes: billed, oneWay }) : fill(t('workOrder.travel.basisCappedNoLookup'), { minutes: billed });
    else basis = oneWay !== null ? fill(t('workOrder.travel.basisLookup'), { minutes: billed, oneWay }) : fill(t('workOrder.travel.basis'), { minutes: billed });
  }
  return (
    <div className={styles.travelFacts} data-travel-facts="true">
      <p className={styles.hint} data-travel-fact="from">{fill(t('workOrder.travel.from'), { place: place(travel.originSiteName, facts.originAddress) })}</p>
      <p className={styles.hint} data-travel-fact="to">{fill(t('workOrder.travel.to'), { place: place(document.siteName, facts.siteAddress) })}</p>
      {miles !== null ? <p className={styles.hint} data-travel-fact="distance">{fill(t('workOrder.travel.distance'), { miles })}</p> : null}
      {basis ? <p className={styles.hint} data-travel-fact="basis">{basis}</p> : null}
      {facts.entered ? (
        <>
          <p className={styles.hint} data-travel-fact="entered">{fill(t('workOrder.travel.enteredByHand'), { person: facts.entered.byName ?? t('workOrder.travel.enteredByUnknown'), when: when(facts.entered.at) })}</p>
          {replaced !== null && replaced !== undefined ? (
            <p className={styles.hint} data-travel-fact="replaced">
              {fill(t(facts.entered.replacedSource === 'Computed' || facts.entered.replacedSource === 'Cached' ? 'workOrder.travel.replacedLookup' : 'workOrder.travel.replaced'), { minutes: replaced })}
            </p>
          ) : null}
        </>
      ) : null}
      {lookup ? (
        <div className={`${styles.hint} ${styles.travelFactRow}`} data-travel-fact="lookup">
          <span>{fill(t(service ? 'workOrder.travel.lookedUpThrough' : 'workOrder.travel.lookedUp'), { when: when(lookup.at), service: service ? t(service) : '' })}</span>
          {lookup.service && facts.mayOpenOutsideCalls ? <Button size={Size.Small} text={[t('workOrder.travel.outsideCalls')]} actions={[() => navigate(outsideCallsRoute({ provider: lookup.service }))]} /> : null}
        </div>
      ) : null}
      {travel.line && billed !== null ? (
        <p className={styles.hint} data-travel-fact="rule">
          {facts.roundingIncrementMinutes !== null
            ? fill(t('workOrder.travel.rule'), { rate: cash(travel.line.rateCents), increment: facts.roundingIncrementMinutes, minutes: billed, hours: hours ?? 0 })
            : fill(t('workOrder.travel.ruleNoRounding'), { rate: cash(travel.line.rateCents), minutes: billed, hours: hours ?? 0 })}
        </p>
      ) : null}
      {travel.line && document.totals.minimumSaleQuantity ? <p className={styles.hint} data-travel-fact="minimum">{fill(t('workOrder.travel.minimum'), { hours: document.totals.minimumSaleQuantity })}</p> : null}
      {travel.resolved ? (
        <p className={styles.hint} data-travel-fact="billing" data-travel-billed={facts.billing.billed ? 'true' : 'false'}>
          {fill(t(facts.billing.reason ? BILLING_LABEL[facts.billing.reason] : 'workOrder.travel.billed'), { minutes: facts.billing.capMinutes ?? 0 })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The travel line (piece 22): the origin, the snapshotted round trip and the
 * line it bills as. 🔨 A figure that never resolved says so and is never drawn
 * as zero; the invoice is what refuses.
 */
export function WorkOrderTravel({ document, apiBaseUrl = '', onChanged, onError }: PanelProps) {
  const cash = cashIn(document.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const [overriding, setOverriding] = useState<{ minutes: string; reason: string } | null>(null);
  /** 🔒 Decision 3063: a refused override reads inside the window, which stays open for the correction. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const travel = document.travel;
  const wrote = (outcome: Outcome) => {
    if (outcome.ok) onChanged();
    else onError(outcome.message);
  };
  const shut = () => {
    setRefusal(null);
    setOverriding(null);
  };
  return (
    <div data-work-order-travel={document.workOrderNumber} data-travel-resolved={travel.resolved ? 'true' : 'false'}>
      <p>{line(travel.originSiteName, '→', travel.billedTravelMinutes === null ? '' : `${travel.billedTravelMinutes} min`)}</p>
      {travel.resolved ? null : <p className={styles.problem} data-travel-unresolved="true">{t('workOrder.travel.unresolved')}</p>}
      {travel.line ? <p className={styles.hint}>{line(travel.line.description, '·', `${travel.line.hours} h`, '@')} {cash(travel.line.rateCents)} {line('=')} {cash(travel.line.amountCents)}</p> : null}
      {travel.travelOverrideReason ? <p className={styles.hint} data-travel-reason="true">{fill(t('workOrder.travel.reasonLine'), { reason: travel.travelOverrideReason })}</p> : null}
      {travel.facts ? <TravelFacts document={document} facts={travel.facts} /> : null}
      {document.editable ? (
        <div className={styles.actions}>
          <Button size={Size.Small} text={[t('workOrder.travel.recompute')]} actions={[async () => wrote(await post(`${WORK_ORDERS_PATH}/${document.id}/travel/recompute`, {}, apiBaseUrl))]} />
          <Button size={Size.Small} text={[t('workOrder.travel.override')]} actions={[() => setOverriding({ minutes: '', reason: '' })]} />
        </div>
      ) : null}
      <FormFrame
        host="window"
        open={overriding !== null}
        title={t('workOrder.travel.override')}
        onCancel={shut}
        dataForm="work-order-travel"
        typed={overriding !== null && (overriding.minutes !== '' || overriding.reason !== '')}
        // 🔨 An override is a person deciding to charge differently and saying why; the reason is required with the figure.
        act={{
          label: t('settings.save'),
          disabled: !overriding?.minutes.trim() || !overriding?.reason.trim(),
          onAct: async () => {
            const outcome = await post(`${WORK_ORDERS_PATH}/${document.id}/travel`, { minutes: Number(overriding?.minutes ?? 0), reason: overriding?.reason ?? '', manual: !travel.resolved }, apiBaseUrl);
            if (!outcome.ok) return setRefusal(outcome.message);
            shut();
            wrote(outcome);
          },
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <TextInput size={Size.Small} label={t('workOrder.travel.minutes')} value={overriding?.minutes ?? ''} onChange={(value) => setOverriding((was) => (was ? { ...was, minutes: value } : was))} name="travel-minutes" required />
        <TextInput size={Size.Large} label={t('workOrder.travel.reason')} value={overriding?.reason ?? ''} onChange={(value) => setOverriding((was) => (was ? { ...was, reason: value } : was))} name="travel-reason" required />
      </FormFrame>
    </div>
  );
}

/**
 * The footer (piece 22): the moves the Work Order transition table allows from
 * the stored status and nothing else. 🔨 Schedule books the visit and Create
 * Follow-Up raises a second job; both are writes of their own rather than
 * status moves, and Void asks for its reason before it writes.
 *
 * 🔒 It draws its controls and no bar (T181). The one document action bar is
 * built once, by the document view page, for every accounting document; the Work
 * Order drew a second copy of it here until it became the tenth of them, and
 * Edit — which stands at that bar's right — went with it.
 */
/** The Book the visit window's draft: the wall-clock pair, a move's reason, and the workers it names (T308). */
interface Booking {
  startsAt: string;
  endsAt: string;
  reason: string;
  workers: Chip[];
  /** 🔒 Decision 1901: the one lead among the workers named, the first of them until another is chosen. */
  lead: string;
  /** 🔒 Decision 3100: Fixed with its typed reason, or Can move. */
  fixed: boolean;
  fixedReason: string;
}
/** The reason a hold names, in words; a reason this build does not know reads as it came. */
const HOLD_REASON_LABELS: Readonly<Record<string, string>> = {
  Parts: t('workOrder.hold.reasonParts'),
  Customer: t('workOrder.hold.reasonCustomer'),
  Weather: t('workOrder.hold.reasonWeather'),
  Access: t('workOrder.hold.reasonAccess'),
};
const holdReasonLabel = (reason: string): string => HOLD_REASON_LABELS[reason] ?? reason;

/** 🔒 Decision 12820 (T3853): a footer refusal and the control that sent it, a move (Complete, Start Work, Close, Resume, Submit for Approval) or Raise an Invoice. */
interface FooterRefusal {
  message: string;
  from: 'move' | 'raise';
}

/** The data hook each sender's banner keeps, so a suite finds the one drawing by the control behind it. */
const FOOTER_REFUSAL_HOOKS: Readonly<Record<FooterRefusal['from'], Record<string, string>>> = {
  move: { 'data-work-order-move-refusal': 'true' },
  raise: { 'data-work-order-raise-refusal': 'true' },
};
/** How long a hold has stood: minutes under an hour, hours under two days, days after. */
function heldFor(minutes: number): string {
  if (minutes < 60) return fill(t('workOrder.hold.minutes'), { n: String(minutes) });
  if (minutes < 2880) return fill(t('workOrder.hold.hours'), { n: String(Math.floor(minutes / 60)) });
  return fill(t('workOrder.hold.days'), { n: String(Math.floor(minutes / 1440)) });
}
const EMPTY_BOOKING: Booking = { startsAt: '', endsAt: '', reason: '', workers: [], lead: '', fixed: false, fixedReason: '' };

export function WorkOrderFooter({ document, apiBaseUrl = '', onChanged, onError, timeZone, offersRateWorking = false, canApprove = false }: PanelProps & { timeZone: string; offersRateWorking?: boolean; canApprove?: boolean }) {
  const cash = cashIn(document.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const options = useWorkOrderOptions(document.companyId, apiBaseUrl);
  const answer = options.state.kind === 'ready' ? options.state.data : null;
  // 🔒 Decision 5570: Schedule opens the Visits tile's inline form through `act=schedule`; this window stays for a job booked before visits were written.
  const [params, setParams] = useSearchParams();
  const [booking, setBooking] = useState<Booking | null>(null);
  /**
   * 🔒 Assigned workers (T308; decision 0872): the organisation's workers as
   * chips, each with their level. Add New Worker is the one Add New host's
   * employee form (T311; decision 0874): the person form, then the word that a
   * person is booked once HR hires them, rather than pretending they were.
   */
  const workerSearch = useMemo(() => searchWithin((answer?.workers ?? []).map((worker) => ({ id: worker.employmentId, line1: worker.name, line2: worker.skillLevelName ?? t('workOrder.line.unassignable') }))), [answer]);
  const addNew = useAddNew(apiBaseUrl);
  /** 🔒 Decision 3063: a refused write reads inside the window that sent it, which stays open for the correction. One window is open at a time, so one refusal serves them. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const closeBooking = () => {
    setRefusal(null);
    setBooking(null);
    setWarning(null);
  };
  /** A window's own write: refused, it reads inside the window; written, the window closes and the page reads again. */
  const settle = (outcome: Outcome, close: () => void) => {
    if (!outcome.ok) return setRefusal(outcome.message);
    close();
    onChanged();
  };
  const shut = (close: () => void) => () => {
    setRefusal(null);
    close();
  };
  /**
   * 🔨 A Scheduled job's move asks for the re-derived tier first; a move that
   * changes the tier, and so the rate, shows the warning and is confirmed by a
   * second press naming the tier it read (piece 29).
   */
  const saveBooking = async () => {
    if (!booking) return;
    const body = { startsAt: booking.startsAt, endsAt: booking.endsAt || null, timeZone, rescheduleReason: booking.reason || null, workerEmploymentIds: booking.workers.map((chip) => chip.id), leadEmploymentId: booking.lead || null, ...fixedOf(booking) };
    // 🔒 Decision 4340: a Dispatched job's booking moves as a Scheduled one's does, since booking its crew sent it.
    const movesBooking = document.status === 'Scheduled' || document.status === 'Dispatched';
    if (movesBooking && (!warning || warning.startsAt !== booking.startsAt)) {
      const answered = await get<{ changes: boolean; toTierId: string; fromTierName: string; toTierName: string; fromModifier: number; toModifier: number }>(`${WORK_ORDERS_PATH}/${document.id}/reschedule-preview?startsAt=${encodeURIComponent(booking.startsAt)}&timeZone=${encodeURIComponent(timeZone)}`, apiBaseUrl);
      if (!answered.ok) return setRefusal(answered.message);
      if (answered.data?.changes) return setWarning({ startsAt: booking.startsAt, ...answered.data });
    }
    setRefusal(null);
    settle(await post(`${WORK_ORDERS_PATH}/${document.id}/schedule`, { ...body, acknowledgedTierId: warning?.startsAt === booking.startsAt ? warning.toTierId : null }, apiBaseUrl), closeBooking);
  };
  const [followUp, setFollowUp] = useState<{ reasonCodeId: string } | null>(null);
  const [voiding, setVoiding] = useState<{ reason: string } | null>(null);
  // 🔒 Decision 12700: Delete asks first; the server refuses a job carrying anything, by name, inside this window.
  const [deleting, setDeleting] = useState(false);
  // 🔒 Decision 12970: the window reads why Delete refuses, and Delete Anyway for the holder, when it opens.
  const [deletion, setDeletion] = useState<WorkOrderDeletion | 'unread' | null>(null);
  const openDelete = async () => {
    setDeletion(null);
    setDeleting(true);
    const answer = await get<WorkOrderDeletion>(workOrderDeletionPath(document.id), apiBaseUrl);
    setDeletion(answer.ok && answer.data ? answer.data : 'unread');
  };
  // 🔒 Decision 2611 (T1170): Close Without Billing asks why the job bills nothing.
  const [writingOff, setWritingOff] = useState<{ reason: string } | null>(null);
  const dateFormat = useDateFormat();
  // 🔒 Put On Hold names what the job waits on, with an optional note (T1300; piece 29).
  const [holding, setHolding] = useState<{ reason: string; note: string } | null>(null);
  // Piece 29: a manager's priority override, and the reschedule warning read before a move is confirmed.
  const [overriding, setOverriding] = useState<{ tierId: string; reason: string } | null>(null);
  const [warning, setWarning] = useState<{ startsAt: string; toTierId: string; fromTierName: string; toTierName: string; fromModifier: number; toModifier: number } | null>(null);
  const [working, setWorking] = useState<{ lineId: string; description: string; working: string[] }[] | null>(null);
  // 🔒 Decision 2813: Reschedule opens Edit Visit on the job's first open visit, keyed to it so a reopening reads it afresh.
  const [moving, setMoving] = useState<WorkOrderVisitRow | null>(null);
  // 🔒 Decision 5571: the job's lines, which the Reschedule's Edit Visit ticks.
  const [movingLines, setMovingLines] = useState<VisitLineOption[]>([]);
  /**
   * 🔒 Decision 2813: Reschedule reads the job's visits and opens Edit Visit on
   * the first still open, prefilled with its window, crew and lead. A job booked
   * before visits were written has none, and keeps the booking window it had.
   */
  const reschedule = async () => {
    const answered = await get<WorkOrderVisitsAnswer>(workOrderVisitsPath(document.id), apiBaseUrl);
    if (!answered.ok) return onError(answered.message);
    const open = (Array.isArray(answered.data?.visits) ? answered.data.visits : []).filter((visit) => visitIsOpen(visit.status)).sort((a, b) => a.visitNumber - b.visitNumber)[0];
    setMovingLines(Array.isArray(answered.data?.lines) ? answered.data.lines : []);
    if (open) return setMoving(open);
    setBooking(EMPTY_BOOKING);
  };

  const navigate = useNavigate();
  const [raising, setRaising] = useState(false);
  // 🔒 Decision 12820 (T3848; T3853): one refusal and the control that sent it, drawn once as a banner in the footer bar above its buttons
  // (the bar draws whatever holds no sized button on a line of its own first, decision 1452), never in the page's notice at the top of a scrolled page.
  const [footerRefusal, setFooterRefusal] = useState<FooterRefusal | null>(null);
  const moved = (outcome: Outcome) => {
    if (outcome.ok) {
      setFooterRefusal(null);
      onChanged();
    } else setFooterRefusal({ message: outcome.message, from: 'move' });
  };
  /**
   * 🔒 Raise an Invoice on a finished job (decision 2594; T1003, T1171): a time and
   * materials job is invoiced from here and never from its Sales Order. The Draft
   * opens as a create always does. 🔒 A refusal reads on the footer beside the
   * control that sent it, never in the page's notice above the fold (T1003, the
   * rule decision 3063 keeps for a window): travel unresolved, time awaiting
   * approval named by worker and day, or a part with no sell price.
   */
  const raiseInvoice = async () => {
    setRaising(true);
    // 🔒 Decision 12820: a footer press ends a standing refusal of another footer move, as a move ends a Raise refusal.
    setFooterRefusal(null);
    const answer = await post<{ id: string }>(invoiceFromWorkOrderPath(document.id), {}, apiBaseUrl);
    setRaising(false);
    if (!answer.ok) return setFooterRefusal({ message: answer.message, from: 'raise' });
    navigate(`${LIST_PAGES.Invoice.route}/${answer.data.id}`);
  };
  const act = async (action: WorkOrderAction) => {
    setRefusal(null);
    // 🔒 Any other footer action ends a standing footer refusal, a Raise an Invoice's included (T2361), as the next Raise or a Dismiss does.
    setFooterRefusal(null);
    if (action === 'schedule') {
      // 🔒 Decision 5570: the Visits tile's inline form books the first visit; a layout drawing no Visits tile keeps this window.
      if (!globalThis.document?.querySelector(`[data-work-order-visits="${document.id}"]`)) return setBooking(EMPTY_BOOKING);
      const next = new URLSearchParams(params);
      next.set('act', 'schedule');
      return setParams(next, { replace: true });
    }
    if (action === 'createFollowUp') return setFollowUp({ reasonCodeId: '' });
    if (action === 'void') return setVoiding({ reason: '' });
    if (action === 'delete') return void openDelete();
    if (action === 'closeWithoutBilling') return setWritingOff({ reason: '' });
    if (action === 'hold') return setHolding({ reason: '', note: '' });
    if (action === 'resume') return moved(await post(`${WORK_ORDERS_PATH}/${document.id}/resume`, { version: document.version }, apiBaseUrl));
    if (action === 'submit') return moved(await post(`${WORK_ORDERS_PATH}/${document.id}/submit`, {}, apiBaseUrl));
    return moved(await post(`${WORK_ORDERS_PATH}/${document.id}/actions/${action}`, {}, apiBaseUrl));
  };
  const showWorking = async () => {
    if (working) return setWorking(null);
    const answered = await get<{ lines: { lineId: string; description: string; working: string[] }[] }>(`${WORK_ORDERS_PATH}/${document.id}/rate-working`, apiBaseUrl);
    if (!answered.ok) return onError(answered.message);
    setWorking(answered.data?.lines ?? []);
  };

  return (
    <>
      {/* 🔒 The approval decision on the document it is about (decision 0599, on decision 0504's reasoning). */}
      <ApprovalDecision documentType="WorkOrder" documentId={document.id} canApprove={canApprove} apiBaseUrl={apiBaseUrl} onDone={() => onChanged()} onError={onError} />
      <span className={styles.chips} data-work-order-moves={document.status}>
        {/* 🔨 Edit is the action bar's primary above the two views and is drawn there alone (decision 0612). */}
        {document.actions.map((action) => (
          <Button
            key={action}
            size={Size.Medium}
            color={action === 'void' || action === 'delete' ? Color.Inactive : Color.Navy}
            text={[t(WORK_ORDER_ACTION_KEYS[action])]}
            actions={[() => void act(action)]}
          />
        ))}
      </span>
      {footerRefusal ? (
        <div {...FOOTER_REFUSAL_HOOKS[footerRefusal.from]}>
          <AlertBanner tone="error" message={footerRefusal.message} action={{ label: t('modal.dismiss'), onAction: () => setFooterRefusal(null) }} />
        </div>
      ) : null}
      {/* 🔨 A Complete job with nothing left to bill draws no Raise an Invoice, since the invoice would only refuse (T1820); a Closed job keeps it. */}
      {(document.status === 'Complete' && document.unbilledText) || document.status === 'Closed' ? (
        <span data-work-order-raise-invoice={document.id}>
          <Button size={Size.Medium} color={Color.Navy} text={[t('jobBilling.raiseInvoice')]} actions={[() => void raiseInvoice()]} disabled={raising} />
        </span>
      ) : null}
      {/* 🔒 Decision 2594 (T1175): past the customer PO's amount the job says so first, so work stops before it goes unpaid. */}
      {document.customerPoWarning ? <p className={styles.problem} data-work-order-po-warning="true">{document.customerPoWarning}</p> : null}
      {/* 🔒 Decision 2611 (T1170): a Complete job names what it has not invoiced, which Close waits on; a job closed without billing says why, who and when. */}
      {document.status === 'Complete' && document.unbilledText ? <p className={styles.hint} data-work-order-unbilled="true">{fill(t('jobClose.owes'), { unbilled: document.unbilledText ?? '' })}</p> : null}
      {document.nonBillable ? (
        <p className={styles.hint} data-work-order-non-billable="true">
          {fill(t('jobClose.closedWithout'), { person: document.nonBillable?.byName ?? t('workOrder.new.noneYet'), at: formatDateTime(document.nonBillable!.at, timeZone, dateFormat), reason: document.nonBillable?.reason ?? '' })}
        </p>
      ) : null}
      {document.hold ? <p className={styles.hint} data-work-order-hold={document.hold.reason}>{line(fill(t('workOrder.hold.banner'), { reason: holdReasonLabel(document.hold.reason), duration: heldFor(document.hold.heldMinutes) }), document.hold.note ?? '')}</p> : null}
      <span className={styles.bannerNumber}>{line(`${document.totals.labourHours} h`, '·')} {cash(document.totals.billedLabourCents)}</span>
      {document.editable ? <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.priority.override')]} actions={[() => setOverriding({ tierId: '', reason: '' })]} /> : null}
      {/* Piece 29: a booked job moves through the same booking, which reads the rate change before it confirms; a job sent to its crew moves the same way (decision 4340). */}
      {(document.status === 'Scheduled' || document.status === 'Dispatched') && document.editable ? <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.reschedule.open')]} actions={[() => void reschedule()]} /> : null}
      {/* 🔨 The sheet's own control moved to the action bar above the two views, so one document offers it once (decision 0612). */}
      {/* The per-line working sits behind the super-admin control; the route refuses anybody else (decision 0375). */}
      {offersRateWorking ? <Button size={Size.Small} text={[working ? t('workOrder.rateWorkingHide') : t('workOrder.rateWorking')]} actions={[() => void showWorking()]} /> : null}
      {working ? (
        <ul className={styles.plainList} data-rate-working="true">
          {working.map((row) => (
            <li key={row.lineId} data-rate-working-line={row.lineId}>{line(row.description, '—', row.working.join(' · '))}</li>
          ))}
        </ul>
      ) : null}

      <FormFrame
        host="window"
        open={booking !== null}
        title={t('workOrder.schedule.title')}
        onCancel={closeBooking}
        dataForm="work-order-schedule"
        typed={booking !== null && (booking.startsAt !== '' || booking.endsAt !== '' || booking.reason !== '' || booking.workers.length > 0 || booking.fixed || booking.fixedReason !== '')}
        act={{
          disabled: !booking?.startsAt || (booking !== null && fixedWaits(booking)),
          label: warning && warning.startsAt === booking?.startsAt ? t('workOrder.reschedule.confirm') : t('settings.save'),
          onAct: () => saveBooking(),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        {/* The wall clock in the record's own zone; the shell's read names the zone, never the machine's clock (decision 0261). */}
        {/* Starts and Ends are the day-with-a-clock picker, never a typed box; the owner met two empty boxes (2026-10-04). */}
        <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.schedule.start')} value={booking?.startsAt ?? ''} onChange={(value) => setBooking({ ...(booking ?? EMPTY_BOOKING), startsAt: value })} name="schedule-start" required />
        <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.schedule.end')} value={booking?.endsAt ?? ''} onChange={(value) => setBooking({ ...(booking ?? EMPTY_BOOKING), endsAt: value })} name="schedule-end" {...(booking?.startsAt ? { min: booking.startsAt } : {})} />
        <Chips label={t('workOrder.schedule.workers')} entityLabel={t('workOrder.schedule.workerEntity')} value={booking?.workers ?? []} onChange={(workers) => setBooking({ ...(booking ?? EMPTY_BOOKING), workers, lead: workers.some((chip) => chip.id === booking?.lead) ? booking?.lead ?? '' : workers[0]?.id ?? '' })} search={workerSearch} onAddNew={() => addNew.open({ kind: 'employee' }, () => undefined)} />
        <p className={styles.hint} data-schedule-workers-hint="true">{t('workOrder.schedule.workersHint')}</p>
        {/* 🔒 Decision 1901: the visit the booking writes names one lead, who alone completes it and asks for the signature. */}
        {(booking?.workers.length ?? 0) > 0 ? (
          <Select size={Size.Medium} label={t('workOrder.schedule.lead')} value={booking?.lead ?? ''} onChange={(lead) => setBooking({ ...(booking ?? EMPTY_BOOKING), lead })} options={(booking?.workers ?? []).map((chip) => ({ value: chip.id, label: chip.label }))} hint={t('workOrder.schedule.leadHint')} dataAttribute={{ 'data-schedule-lead': 'true' }} required />
        ) : null}
        {/* 🔒 Decision 3100: the visit the booking writes Can move or is Fixed with its reason. */}
        <VisitMovableFields booking={booking ?? EMPTY_BOOKING} onChange={(next) => setBooking({ ...(booking ?? EMPTY_BOOKING), ...next })} name="schedule-movable" />
        {document.status === 'Scheduled' || document.status === 'Dispatched' ? (
          <TextInput size={Size.Large} label={t('workOrder.visits.moveReason')} value={booking?.reason ?? ''} onChange={(value) => setBooking({ ...(booking ?? EMPTY_BOOKING), reason: value })} name="schedule-reason" required />
        ) : null}
        {/* 🔒 Piece 29: the rate change a move makes, read before the new date is confirmed. */}
        {warning && warning.startsAt === booking?.startsAt ? (
          <p className={styles.problem} data-reschedule-warning={warning.toTierName}>{line(t('workOrder.reschedule.warning'), warning.fromTierName, `×${warning.fromModifier}`, '→', warning.toTierName, `×${warning.toModifier}`)}</p>
        ) : null}
      </FormFrame>

      {addNew.host}

      {moving !== null ? (
        <EditVisitModal
          key={`${moving.id}:${moving.version}`}
          workOrderId={document.id}
          companyId={document.companyId}
          apiBaseUrl={apiBaseUrl}
          visit={{ ...moving, carriesBooking: moving.carriesBooking === true }}
          timeZone={timeZone}
          lines={movingLines}
          onClose={() => setMoving(null)}
          onSaved={() => {
            setMoving(null);
            onChanged();
          }}
        />
      ) : null}

      <FormFrame
        host="window"
        open={overriding !== null}
        title={t('workOrder.priority.override')}
        onCancel={shut(() => setOverriding(null))}
        dataForm="work-order-priority"
        typed={overriding !== null && (overriding.tierId !== '' || overriding.reason !== '')}
        act={{
          label: t('settings.save'),
          // 🔨 A derived priority is overridden only with the reason the override report reads (decision 0734).
          disabled: !overriding?.tierId || !overriding?.reason.trim(),
          onAct: async () => settle(await post(`${WORK_ORDERS_PATH}/${document.id}/priority`, { priorityTierId: overriding?.tierId, reason: overriding?.reason }, apiBaseUrl), shut(() => setOverriding(null))),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <p className={styles.hint}>{line(t('workOrder.priority.current'), document.priorityTierName)}</p>
        <Select size={Size.Medium}
          label={t('workOrder.priority.tier')}
          value={overriding?.tierId ?? ''}
          onChange={(value) => setOverriding({ tierId: value, reason: overriding?.reason ?? '' })}
          placeholder={t('workOrder.new.noneYet')}
          options={(answer?.tiers ?? []).map((tier) => ({ value: tier.id, label: line(tier.name, `×${tier.billingModifier}`) }))}
          dataAttribute={{ 'data-priority-tier': 'true' }}
          required
        />
        <TextInput size={Size.Large} label={t('workOrder.priority.reason')} value={overriding?.reason ?? ''} onChange={(value) => setOverriding({ tierId: overriding?.tierId ?? '', reason: value })} name="priority-reason" required />
      </FormFrame>

      <FormFrame
        host="window"
        open={followUp !== null}
        title={t('workOrder.followUp.title')}
        onCancel={shut(() => setFollowUp(null))}
        dataForm="work-order-follow-up"
        typed={followUp !== null && followUp.reasonCodeId !== ''}
        act={{
          label: t('workOrder.action.createFollowUp'),
          disabled: !followUp?.reasonCodeId,
          onAct: async () => settle(await post(`${WORK_ORDERS_PATH}/${document.id}/follow-up`, { followUpReasonCodeId: followUp?.reasonCodeId }, apiBaseUrl), shut(() => setFollowUp(null))),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <p className={styles.hint}>{t('workOrder.followUp.lines')}</p>
        <Select size={Size.Medium}
          label={t('workOrder.followUp.reason')}
          value={followUp?.reasonCodeId ?? ''}
          onChange={(value) => setFollowUp({ reasonCodeId: value })}
          placeholder={t('workOrder.new.noneYet')}
          options={(answer?.reasonCodes ?? []).map((code) => ({ value: code.id, label: line(code.label, `· ${code.isBillable ? t('settings.followUp.billableShort') : t('settings.followUp.absorbedShort')}`) }))}
          dataAttribute={{ 'data-follow-up-reason': 'true' }}
          required
        />
      </FormFrame>

      <FormFrame
        host="window"
        open={holding !== null}
        title={t('workOrder.hold.title')}
        onCancel={shut(() => setHolding(null))}
        dataForm="work-order-hold"
        typed={holding !== null && (holding.reason !== '' || holding.note !== '')}
        act={{
          label: t('workOrder.action.hold'),
          disabled: !holding?.reason,
          onAct: async () => settle(await post(`${WORK_ORDERS_PATH}/${document.id}/hold`, { reason: holding?.reason, note: holding?.note || null, version: document.version }, apiBaseUrl), shut(() => setHolding(null))),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <Select size={Size.Medium}
          label={t('workOrder.hold.reason')}
          value={holding?.reason ?? ''}
          onChange={(value) => setHolding({ reason: value, note: holding?.note ?? '' })}
          placeholder={t('workOrder.new.noneYet')}
          options={(document.holdReasons ?? []).map((reason) => ({ value: reason, label: holdReasonLabel(reason) }))}
          dataAttribute={{ 'data-hold-reason': 'true' }}
          required
        />
        <TextInput size={Size.Large} label={t('workOrder.hold.note')} value={holding?.note ?? ''} onChange={(value) => setHolding({ reason: holding?.reason ?? '', note: value })} name="hold-note" />
      </FormFrame>

      <FormFrame
        host="window"
        open={voiding !== null}
        title={t('workOrder.void.title')}
        onCancel={shut(() => setVoiding(null))}
        dataForm="work-order-void"
        cancelLabel={t('workOrder.void.keep')}
        typed={voiding !== null && voiding.reason !== ''}
        act={{
          label: t('workOrder.action.void'),
          disabled: !voiding?.reason.trim(),
          onAct: async () => settle(await post(`${WORK_ORDERS_PATH}/${document.id}/actions/void`, { reason: voiding?.reason }, apiBaseUrl), shut(() => setVoiding(null))),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        <TextInput size={Size.Large} label={t('workOrder.void.reason')} value={voiding?.reason ?? ''} onChange={(value) => setVoiding({ reason: value })} name="void-reason" required />
      </FormFrame>

      <FormFrame
        host="window"
        open={deleting}
        title={t('workOrder.delete.title')}
        onCancel={shut(() => setDeleting(false))}
        dataForm="work-order-delete"
        typed={false}
        act={{
          label: t('workOrder.delete.confirm'),
          ...deleteGate(deletion),
          onAct: async () => {
            const answer = await post<{ deleted: true }>(workOrderDeletePath(document.id), {}, apiBaseUrl);
            if (!answer.ok) return setRefusal(answer.message);
            setDeleting(false);
            navigate(LIST_PAGES.WorkOrder.route, { replace: true });
          },
        }}
        {...(anywayOf(deletion) ? { secondary: {
          label: t('workOrder.delete.anyway'),
          disabled: anywayOf(deletion)!.refusal !== null,
          ...(anywayOf(deletion)!.refusal ? { disabledReason: anywayOf(deletion)!.refusal! } : {}),
          destructive: { subject: anywayOf(deletion)!.subject, phrase: t('modal.deletePhrase'), toTrash: true },
          onAct: async () => {
            const answer = await post<{ deleted: true }>(workOrderDeleteAnywayPath(document.id), {}, apiBaseUrl);
            if (!answer.ok) return setRefusal(answer.message);
            setDeleting(false);
            navigate(LIST_PAGES.WorkOrder.route, { replace: true });
          },
        } } : {})}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        {deletion === null ? <p className={styles.hint}>{t('workOrder.delete.reading')}</p> : null}
        {deletion === 'unread' ? <p className={styles.problem} data-work-order-delete-unread="true">{t('workOrder.delete.unread')}</p> : null}
        {deletion !== null && deletion !== 'unread' && deletion.problem === null ? <p>{fill(t('workOrder.delete.lead'), { number: document.workOrderNumber })}</p> : null}
        {deletion !== null && deletion !== 'unread' && deletion.problem !== null ? <p className={styles.problem} data-work-order-delete-problem="true">{deletion.problem}</p> : null}
        {anywayOf(deletion) ? <p className={styles.hint} data-work-order-delete-anyway={anywayOf(deletion)!.refusal ? 'refused' : 'stands'}>{anywayOf(deletion)!.refusal ?? fill(t('workOrder.delete.anywayLead'), { subject: anywayOf(deletion)!.subject })}</p> : null}
      </FormFrame>

      <FormFrame
        host="window"
        open={writingOff !== null}
        title={t('jobClose.title')}
        onCancel={shut(() => setWritingOff(null))}
        dataForm="work-order-close-without-billing"
        typed={writingOff !== null && writingOff.reason !== ''}
        act={{
          label: t('jobClose.confirm'),
          disabled: !writingOff?.reason.trim(),
          onAct: async () => settle(await post(`${WORK_ORDERS_PATH}/${document.id}/actions/closeWithoutBilling`, { reason: writingOff?.reason }, apiBaseUrl), shut(() => setWritingOff(null))),
        }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        {document.unbilledText ? <p className={styles.hint}>{fill(t('jobClose.owes'), { unbilled: document.unbilledText ?? '' })}</p> : null}
        <TextInput size={Size.Large} label={t('jobClose.reason')} value={writingOff?.reason ?? ''} onChange={(value) => setWritingOff({ reason: value })} name="close-without-billing-reason" required />
        <p className={styles.hint}>{t('jobClose.reasonHint')}</p>
      </FormFrame>
    </>
  );
}

/** 🔒 Decision 12970: the ordinary Delete stands only once the window has read that nothing refuses it, and says why where something does. */
function deleteGate(deletion: WorkOrderDeletion | 'unread' | null): { disabled?: boolean; disabledReason?: string } {
  if (deletion === null) return { disabled: true, disabledReason: t('workOrder.delete.reading') };
  if (deletion === 'unread') return { disabled: true, disabledReason: t('workOrder.delete.unread') };
  return deletion.problem === null ? {} : { disabled: true, disabledReason: deletion.problem };
}

/** Delete Anyway, drawn where the server answered it to the holder and only while the ordinary Delete refuses. */
function anywayOf(deletion: WorkOrderDeletion | 'unread' | null): WorkOrderDeletion['anyway'] {
  return deletion !== null && deletion !== 'unread' && deletion.problem !== null ? deletion.anyway : null;
}
